import React, { useState, useEffect } from 'react';
import { Download, X, RefreshCw, Smartphone, Monitor } from 'lucide-react';
import { usePWA } from '../../hooks/usePWA';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

const PWAInstallPrompt = () => {
  const { 
    canInstall, 
    isInstalled, 
    updateAvailable, 
    installPWA, 
    updateApp,
    getPWASupport,
    getDeviceInfo
  } = usePWA();
  const device = useDeviceDetection();
  
  const [isOpen, setIsOpen] = useState(false);
  
  // Высота BottomNavigation (64px = 16 * 4)
  const bottomNavHeight = device.isMobile && device.isPortrait ? 64 : 0;
  const [isInstalling, setIsInstalling] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null); // Для отладки на мобильных
  const [diagnosticsVisible, setDiagnosticsVisible] = useState(false); // Показывать ли диагностику на экране
  const [diagnosticsData, setDiagnosticsData] = useState(null); // Данные диагностики для отображения

  // Проверяем, был ли промпт уже отклонен
  const wasDismissed = localStorage.getItem('pwa_prompt_dismissed') === 'true';
  
  // Проверяем, была ли попытка установки (для Opera/Firefox, где может не быть beforeinstallprompt)
  // Сбрасываем флаг через час, чтобы пользователь мог попробовать снова
  // НО: не блокируем показ баннера, если установка не удалась - пользователь может попробовать снова
  const installAttemptedTimestamp = localStorage.getItem('pwa_install_attempted_timestamp');
  const installAttemptedTime = installAttemptedTimestamp ? (Date.now() - parseInt(installAttemptedTimestamp, 10)) : null;
  // Блокируем показ только если была недавняя попытка (менее 5 минут назад) И установка не удалась
  // Это предотвращает спам баннера, но позволяет показать его снова через некоторое время
  const installAttempted = installAttemptedTime !== null && installAttemptedTime < 300000; // 5 минут вместо часа

  // Блок отладки (только в development режиме)
  const showDebug = process.env.NODE_ENV === 'development';

  // Проверяем, показан ли баннер согласия на cookies
  // Если consent === null, значит баннер еще показывается
  // Если consent !== null (true или false), значит пользователь уже ответил
  const [analyticsConsentAnswered, setAnalyticsConsentAnswered] = useState(() => {
    const consent = localStorage.getItem('analytics_consent');
    return consent !== null; // Если не null, значит пользователь уже ответил
  });

  // Отслеживаем изменения согласия на cookies
  useEffect(() => {
    const checkConsent = () => {
      const consent = localStorage.getItem('analytics_consent');
      const answered = consent !== null;
      
      // Проверяем, что баннер согласия действительно скрыт (через DOM)
      const consentBanner = document.querySelector('[data-analytics-consent-banner]');
      const isBannerVisible = consentBanner && consentBanner.offsetParent !== null;
      
      // Пользователь ответил на согласие И баннер скрыт
      if (answered && !analyticsConsentAnswered && !isBannerVisible) {
        // Пользователь только что ответил на согласие и баннер закрыт
        setAnalyticsConsentAnswered(true);
        // Устанавливаем userInteracted в true, так как пользователь уже взаимодействовал со страницей
        setUserInteracted(true);
      } else if (!answered && analyticsConsentAnswered) {
        // Согласие было удалено (маловероятно, но на всякий случай)
        setAnalyticsConsentAnswered(false);
      }
    };

    // Проверяем сразу
    checkConsent();

    // Слушаем изменения в localStorage (если баннер закрыт в другой вкладке)
    const handleStorageChange = (e) => {
      if (e.key === 'analytics_consent') {
        // Небольшая задержка, чтобы баннер успел закрыться
        setTimeout(checkConsent, 300);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Проверяем периодически (на случай, если изменения в той же вкладке)
    // Увеличили интервал до 1000ms, чтобы не спамить в консоль
    const interval = setInterval(checkConsent, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [analyticsConsentAnswered]);

  // Отслеживаем взаимодействие пользователя со страницей (на случай, если согласие уже было дано ранее)
  useEffect(() => {
    // Если согласие уже было дано ранее, считаем что пользователь уже взаимодействовал
    if (analyticsConsentAnswered) {
      setUserInteracted(true);
      return;
    }

    const handleUserInteraction = () => {
      setUserInteracted(true);
    };

    // Слушаем различные события взаимодействия
    const events = ['click', 'scroll', 'touchstart', 'keydown'];
    events.forEach(event => {
      window.addEventListener(event, handleUserInteraction, { once: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [analyticsConsentAnswered]);

  // Обновляем информацию для отладки (только в development)
  useEffect(() => {
    if (!showDebug) return;
    
    const updateDebugInfo = () => {
      // Проверяем видимость баннера согласия и промпта
      const consentBanner = document.querySelector('[data-analytics-consent-banner]');
      const pwaPrompt = document.querySelector('[data-pwa-install-prompt]');
      const isBannerVisible = consentBanner ? consentBanner.offsetParent !== null : null;
      const isPromptVisible = pwaPrompt ? pwaPrompt.offsetParent !== null : null;
      
      const debugData = {
        canInstall,
        isInstalled,
        isOpen,
        wasDismissed,
        analyticsConsentAnswered,
        consentValue: localStorage.getItem('analytics_consent'),
        installAttempted,
        installAttemptedTimestamp: installAttemptedTimestamp ? new Date(parseInt(installAttemptedTimestamp, 10)).toLocaleString() : null,
        installAttemptedTimeAgo: installAttemptedTime !== null ? `${Math.round(installAttemptedTime / 1000)}s ago` : null,
        shouldShow: !isInstalled && !isOpen && !wasDismissed && analyticsConsentAnswered && !installAttempted,
        isBannerVisible,
        isPromptVisible,
        isMobile: isMobileDevice()
      };
      
      setDebugInfo(debugData);
    };
    
    updateDebugInfo();
    const interval = setInterval(updateDebugInfo, 500);
    
    return () => clearInterval(interval);
  }, [canInstall, isInstalled, isOpen, wasDismissed, analyticsConsentAnswered, showDebug]);

  // Проверяем, является ли устройство мобильным
  const isMobileDevice = () => {
    if (!deviceInfo) return false;
    const isIOS = /iPad|iPhone|iPod/.test(deviceInfo.userAgent);
    const isAndroid = /Android/.test(deviceInfo.userAgent);
    return isIOS || isAndroid;
  };

  // Показываем промпт после того, как пользователь ответил на согласие cookies
  // Показываем только на мобильных устройствах (не на десктопе)
  // Показываем даже если canInstall === false (для мобильных устройств, где событие может не сработать)
  useEffect(() => {
    // Не показываем на десктопе
    if (!isMobileDevice()) {
      return;
    }

    // Показываем промпт, если:
    // 1. Приложение не установлено
    // 2. Промпт еще не открыт
    // 3. Промпт не был отклонен
    // 4. Пользователь ответил на согласие cookies
    // 5. Не было недавней попытки установки (менее 5 минут назад)
    // canInstall может быть false на мобильных, но установка все равно возможна через меню браузера
    const shouldShowBanner = !isInstalled && !isOpen && !wasDismissed && analyticsConsentAnswered && !installAttempted;
    
    
    if (shouldShowBanner) {
      // Проверяем, что баннер согласия действительно скрыт
      const consentBanner = document.querySelector('[data-analytics-consent-banner]');
      const isBannerVisible = consentBanner && consentBanner.offsetParent !== null;
      
      if (isBannerVisible) {
        return; // Ждем, пока баннер закроется
      }
      
      // Показываем через 2 секунды после того, как пользователь ответил на согласие cookies
      // И баннер согласия закрыт
      const timer = setTimeout(() => {
        // Двойная проверка перед показом
        const banner = document.querySelector('[data-analytics-consent-banner]');
        const bannerVisible = banner && banner.offsetParent !== null;
        
        if (!bannerVisible) {
          setIsOpen(true);
        }
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    } else {
      const reasons = {
        isInstalled: isInstalled ? '✅ Installed' : '❌ Not installed',
        isOpen: isOpen ? '✅ Already open' : '❌ Not open',
        wasDismissed: wasDismissed ? '✅ Was dismissed' : '❌ Not dismissed',
        analyticsConsentAnswered: analyticsConsentAnswered ? '✅ Answered' : '❌ Not answered',
        installAttempted: installAttempted ? `✅ Attempted ${installAttemptedTime !== null ? Math.round(installAttemptedTime / 1000) + 's ago' : ''}` : '❌ Not attempted',
        isMobile: isMobileDevice() ? '✅ Mobile' : '❌ Not mobile'
      };
      
      // Если все условия выполнены кроме installAttempted, и прошло больше 5 минут - сбрасываем флаг
      if (!isInstalled && !isOpen && !wasDismissed && analyticsConsentAnswered && installAttempted && installAttemptedTime !== null && installAttemptedTime > 300000) {
        localStorage.removeItem('pwa_install_attempted_timestamp');
        // Перезапускаем проверку через небольшую задержку
        setTimeout(() => {
          if (!isInstalled && !isOpen && !wasDismissed && analyticsConsentAnswered) {
            const consentBanner = document.querySelector('[data-analytics-consent-banner]');
            const isBannerVisible = consentBanner && consentBanner.offsetParent !== null;
            if (!isBannerVisible) {
              setIsOpen(true);
            }
          }
        }, 500);
      }
    }
  }, [canInstall, isInstalled, isOpen, wasDismissed, analyticsConsentAnswered, installAttempted, installAttemptedTime, deviceInfo]);

  React.useEffect(() => {
    // Получаем информацию об устройстве
    setDeviceInfo(getDeviceInfo());
  }, [getDeviceInfo]);

  // Функция детальной диагностики установки PWA
  const diagnosePWAInstallation = () => {
    const userAgent = navigator.userAgent || '';
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const manifestHref = manifestLink?.href;
    
    // Проверяем манифест
    let manifestData = null;
    let manifestError = null;
    if (manifestHref) {
      fetch(manifestHref)
        .then(res => res.json())
        .then(data => {
        })
        .catch(err => {
        });
    }
    
    // Проверяем Service Worker
    let swRegistration = null;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration()
        .then(reg => {
          if (reg) {
          }
        })
        .catch(() => {
        });
    }
    
    const diagnostics = {
      // Базовые проверки
      userAgent,
      isHTTPS: window.location.protocol === 'https:',
      isLocalhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
      isStandalone: window.matchMedia('(display-mode: standalone)').matches,
      isInstalled: isInstalled,
      
      // Манифест
      hasManifestLink: manifestLink !== null,
      manifestHref: manifestHref,
      
      // Service Worker
      hasServiceWorker: 'serviceWorker' in navigator,
      
      // PWA API
      canInstall: canInstall,
      hasBeforeInstallPrompt: 'BeforeInstallPromptEvent' in window,
      beforeinstallpromptFiredBefore: localStorage.getItem('pwa_beforeinstallprompt_fired') === 'true',
      beforeinstallpromptFireCount: parseInt(localStorage.getItem('pwa_beforeinstallprompt_count') || '0', 10),
      
      // Браузер
      isOpera: /OPR|Opera|OPiOS|OPAndroid/.test(userAgent) || 
              (window.opr && window.opr.addons) || 
              (window.opra && window.opera.toString().indexOf('Opera') !== -1),
      isFirefox: /Firefox/.test(userAgent),
      isChrome: /Chrome/.test(userAgent) && !/OPR|Opera/.test(userAgent),
      isEdge: /Edg/.test(userAgent),
      isSafari: /Safari/.test(userAgent) && !/Chrome/.test(userAgent),
      
      // PWA Support
      pwaSupport: getPWASupport(),
      
      // Дополнительная информация
      deviceInfo: getDeviceInfo(),
      windowLocation: {
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        pathname: window.location.pathname
      }
    };
    
    // Проверка требований для установки
    const requirements = {
      'HTTPS or localhost': diagnostics.isHTTPS || diagnostics.isLocalhost,
      'Manifest link exists': diagnostics.hasManifestLink,
      'Service Worker support': diagnostics.hasServiceWorker,
      'Not already installed': !diagnostics.isInstalled,
      'Not in standalone mode': !diagnostics.isStandalone
    };
    
    
    // Сохраняем данные диагностики для визуального отображения
    setDiagnosticsData({
      ...diagnostics,
      requirements: {
        'HTTPS or localhost': diagnostics.isHTTPS || diagnostics.isLocalhost,
        'Manifest link exists': diagnostics.hasManifestLink,
        'Service Worker support': diagnostics.hasServiceWorker,
        'Not already installed': !diagnostics.isInstalled,
        'Not in standalone mode': !diagnostics.isStandalone
      },
      timestamp: new Date().toLocaleString()
    });
    
    return diagnostics;
  };

  const handleInstall = async () => {
    const userAgent = navigator.userAgent || '';
    const isOpera = /OPR|Opera|OPiOS|OPAndroid/.test(userAgent) || 
                   (window.opr && window.opr.addons) || 
                   (window.opera && window.opera.toString().indexOf('Opera') !== -1);
    const isFirefox = /Firefox/.test(userAgent);
    
    // Всегда запускаем детальную диагностику при попытке установки
    const diagnostics = diagnosePWAInstallation();
    // Показываем визуальную диагностику на мобильных
    if (device.isMobile) {
      setDiagnosticsVisible(true);
    }
    
    // Если доступна установка через API, используем её
    if (canInstall) {
      setIsInstalling(true);
      
      try {
        
        // Не закрываем баннер сразу - показываем состояние установки
        // Это даст пользователю обратную связь, что процесс начался
        
        const result = await installPWA();
        
        if (result) {
          // Показываем сообщение о том, что установка началась
          // Баннер закроется автоматически через событие appinstalled
          // Не закрываем баннер здесь, чтобы пользователь видел прогресс
        } else {
          setIsInstalling(false);
          // Помечаем попытку установки только если была реальная попытка (canInstall = true)
          // Это предотвратит повторный показ баннера сразу после отклонения
          localStorage.setItem('pwa_install_attempted_timestamp', Date.now().toString());
          // Закрываем баннер - инструкции уже показаны в баннере
          setIsOpen(false);
        }
      } catch (error) {
        setIsInstalling(false);
        
        // Помечаем попытку установки только если была реальная попытка (canInstall = true)
        localStorage.setItem('pwa_install_attempted_timestamp', Date.now().toString());
        setIsOpen(false);
      }
    } else {
      // Если установка через API недоступна (canInstall = false)
      setIsInstalling(false);
      
      // Пробуем все равно вызвать installPWA - может быть deferredPrompt появился
      try {
        const result = await installPWA();
        
        if (result) {
          // Установка успешна, баннер закроется через appinstalled
          return;
        }
      } catch (error) {
      }
      
      // Если установка не удалась, устанавливаем флаг и закрываем баннер
      // Это предотвратит повторные нажатия кнопки
      localStorage.setItem('pwa_install_attempted_timestamp', Date.now().toString());
      setIsOpen(false);
    }
  };

  const handleUpdate = () => {
    try {
      updateApp();
    } catch (error) {
      // В случае ошибки просто перезагружаем страницу
      window.location.reload();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Сохраняем, что пользователь отклонил промпт (на 7 дней)
    localStorage.setItem('pwa_prompt_dismissed', 'true');
    const dismissTime = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 дней
    localStorage.setItem('pwa_prompt_dismissed_until', dismissTime.toString());
  };

  // Проверяем, истек ли срок скрытия промпта
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('pwa_prompt_dismissed_until');
    if (dismissedUntil && Date.now() > parseInt(dismissedUntil, 10)) {
      localStorage.removeItem('pwa_prompt_dismissed');
      localStorage.removeItem('pwa_prompt_dismissed_until');
    }
  }, []);

  const getInstallInstructions = () => {
    if (!deviceInfo) return null;

    const userAgent = deviceInfo.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    const isDesktop = !isIOS && !isAndroid;
    
    // Определяем браузер
    const isOpera = /OPR|Opera|OPiOS|OPAndroid/.test(userAgent) || 
                   (window.opr && window.opr.addons) || 
                   (window.opera && window.opera.toString().indexOf('Opera') !== -1);
    const isFirefox = /Firefox/.test(userAgent);
    const isChrome = /Chrome/.test(userAgent) && !isOpera;

    if (isIOS) {
      return {
        title: 'Установка на iOS',
        steps: [
          'Нажмите кнопку "Поделиться" в Safari',
          'Выберите "На экран \"Домой\""',
          'Нажмите "Добавить"'
        ],
        icon: <Smartphone className="h-6 w-6" />
      };
    }

      if (isAndroid) {
        if (isOpera) {
          return {
            title: 'Установка в Opera на Android',
            steps: [
              'Нажмите на меню браузера (три точки)',
              'Выберите "Добавить на главный экран" или "Виджет"',
              'В появившемся окне нажмите и удерживайте иконку',
              'Перетащите виджет на главный экран или нажмите "Добавить"'
            ],
            icon: <Smartphone className="h-6 w-6" />
          };
        }
      
      if (isFirefox) {
        return {
          title: 'Установка в Firefox на Android',
          steps: [
            'Нажмите на меню браузера (три линии)',
            'Найдите "Установить" или "Добавить на главный экран"',
            'Если пункта нет, попробуйте кнопку в адресной строке',
            'Подтвердите установку'
          ],
          icon: <Smartphone className="h-6 w-6" />
        };
      }
      
      return {
        title: 'Установка на Android',
        steps: [
          'Нажмите на меню браузера',
          'Выберите "Добавить на главный экран"',
          'Подтвердите установку'
        ],
        icon: <Smartphone className="h-6 w-6" />
      };
    }

      if (isDesktop) {
        if (isOpera) {
          return {
            title: 'Установка в Opera',
            steps: [
              'Нажмите на меню браузера (три точки)',
              'Найдите "Установить приложение"',
              'Если пункта нет, используйте кнопку в адресной строке',
              'Подтвердите установку'
            ],
            icon: <Monitor className="h-6 w-6" />
          };
        }
      
      if (isFirefox) {
        return {
          title: 'Установка в Firefox',
          steps: [
            'Нажмите на меню браузера (три линии)',
            'Найдите "Установить"',
            'Если пункта нет, используйте кнопку в адресной строке',
            'Подтвердите установку'
          ],
          icon: <Monitor className="h-6 w-6" />
        };
      }
      
      return {
        title: 'Установка на компьютер',
        steps: [
          'Нажмите кнопку "Установить" ниже',
          'Подтвердите установку в браузере',
          'Приложение появится в меню'
        ],
        icon: <Monitor className="h-6 w-6" />
      };
    }

    return null;
  };

  const instructions = getInstallInstructions();
  
  // Для отладки: принудительно показываем баннер, если все условия выполнены, но он не показывается
  useEffect(() => {
    if (showDebug && !isOpen && !isInstalled && !wasDismissed && analyticsConsentAnswered && !installAttempted) {
      const consentBanner = document.querySelector('[data-analytics-consent-banner]');
      const isBannerVisible = consentBanner && consentBanner.offsetParent !== null;
      
      if (!isBannerVisible && isMobileDevice()) {
        const timer = setTimeout(() => {
          if (!isOpen && !isInstalled) {
            setIsOpen(true);
          }
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [showDebug, isOpen, isInstalled, wasDismissed, analyticsConsentAnswered, installAttempted]);
  
  if (!isOpen && !updateAvailable && !showDebug) {
    return null;
  }
  
  return (
    <>
      {/* Визуальный блок диагностики для мобильных */}
      {diagnosticsVisible && diagnosticsData && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                🔍 PWA Installation Diagnostics
              </h3>
              <button
                onClick={() => setDiagnosticsVisible(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4 text-sm">
              {/* Требования */}
              <div>
                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">📋 Requirements Check:</h4>
                <div className="space-y-1">
                  {Object.entries(diagnosticsData.requirements || {}).map(([req, met]) => (
                    <div key={req} className="flex items-center space-x-2">
                      <span>{met ? '✅' : '❌'}</span>
                      <span className={met ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {req}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Браузер */}
              <div>
                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">🌐 Browser:</h4>
                <div className="space-y-1 text-gray-700 dark:text-gray-300">
                  <div>Opera: {diagnosticsData.isOpera ? '✅' : '❌'}</div>
                  <div>Chrome: {diagnosticsData.isChrome ? '✅' : '❌'}</div>
                  <div>Firefox: {diagnosticsData.isFirefox ? '✅' : '❌'}</div>
                  <div>Edge: {diagnosticsData.isEdge ? '✅' : '❌'}</div>
                  <div>Safari: {diagnosticsData.isSafari ? '✅' : '❌'}</div>
                </div>
              </div>
              
              {/* Статус */}
              <div>
                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">📊 Status:</h4>
                <div className="space-y-1 text-gray-700 dark:text-gray-300">
                  <div>HTTPS: {diagnosticsData.isHTTPS ? '✅' : '❌'}</div>
                  <div>Manifest: {diagnosticsData.hasManifestLink ? '✅' : '❌'}</div>
                  <div>Service Worker: {diagnosticsData.hasServiceWorker ? '✅' : '❌'}</div>
                  <div className="flex items-start space-x-2">
                    <span>Can Install: {diagnosticsData.canInstall ? '✅' : '❌'}</span>
                    {!diagnosticsData.canInstall && diagnosticsData.isOpera && (
                      <span className="text-xs text-yellow-600 dark:text-yellow-400">
                        (В Opera событие beforeinstallprompt может не сработать, но установка возможна через виджет)
                      </span>
                    )}
                  </div>
                  <div>Already Installed: {diagnosticsData.isInstalled ? '✅' : '❌'}</div>
                  <div>Standalone Mode: {diagnosticsData.isStandalone ? '✅' : '❌'}</div>
                  {diagnosticsData.beforeinstallpromptFiredBefore !== undefined && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-xs">
                        <div>Event fired before: {diagnosticsData.beforeinstallpromptFiredBefore ? '✅ Да' : '❌ Нет'}</div>
                        {diagnosticsData.beforeinstallpromptFiredBefore && (
                          <div>Fire count: {diagnosticsData.beforeinstallpromptFireCount}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Объяснение для Opera - почему кнопка не работает */}
              {diagnosticsData.isOpera && !diagnosticsData.canInstall && (
                <div className={`${diagnosticsData.beforeinstallpromptFiredBefore ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'} border rounded-lg p-3`}>
                  <h4 className={`font-semibold mb-2 ${diagnosticsData.beforeinstallpromptFiredBefore ? 'text-yellow-800 dark:text-yellow-200' : 'text-red-800 dark:text-red-200'}`}>
                    {diagnosticsData.beforeinstallpromptFiredBefore ? '⚠️ Почему кнопка "Установить" не работает сейчас:' : '❌ Почему кнопка "Установить" не работает:'}
                  </h4>
                  <div className={`text-xs space-y-2 ${diagnosticsData.beforeinstallpromptFiredBefore ? 'text-yellow-700 dark:text-yellow-300' : 'text-red-700 dark:text-red-300'}`}>
                    {diagnosticsData.beforeinstallpromptFiredBefore ? (
                      <>
                        <p>
                          <strong>Интересно:</strong> Событие <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">beforeinstallprompt</code> срабатывало раньше ({diagnosticsData.beforeinstallpromptFireCount} раз)!
                        </p>
                        <p>
                          <strong>Проблема:</strong> Сейчас событие не срабатывает, хотя раньше работало.
                        </p>
                        <p>
                          <strong>Возможные причины:</strong>
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Событие уже было использовано (deferredPrompt был вызван)</li>
                          <li>Приложение уже установлено (или браузер считает, что установлено)</li>
                          <li>Событие было отклонено пользователем ранее</li>
                          <li>Нужны определенные условия (время на странице, взаимодействие пользователя)</li>
                          <li>Service Worker или Manifest еще не готовы</li>
                        </ul>
                        <p>
                          <strong>Решение:</strong> Попробуйте перезагрузить страницу или используйте виджет в меню браузера.
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          <strong>Проблема:</strong> Событие <code className="bg-red-100 dark:bg-red-900 px-1 rounded">beforeinstallprompt</code> еще не срабатывало в Opera.
                        </p>
                        <p>
                          <strong>Возможные причины:</strong>
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Событие может сработать позже (нужно время, взаимодействие пользователя)</li>
                          <li>Opera может не поддерживать событие в этом контексте</li>
                          <li>Service Worker или Manifest еще не готовы</li>
                        </ul>
                        <p>
                          <strong>Решение:</strong> Подождите немного или используйте виджет в меню браузера:
                        </p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Меню браузера (три точки) → "Добавить на главный экран"</li>
                          <li>В появившемся окне нажмите и удерживайте иконку виджета</li>
                          <li>Перетащите на главный экран</li>
                        </ol>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Манифест */}
              {diagnosticsData.manifestHref && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">📄 Manifest:</h4>
                  <div className="text-gray-700 dark:text-gray-300 break-all text-xs">
                    {diagnosticsData.manifestHref}
                  </div>
                </div>
              )}
              
              {/* Время */}
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Generated: {diagnosticsData.timestamp}
              </div>
              
              {/* Объяснение почему кнопка не работает в Opera */}
              {diagnosticsData.isOpera && !diagnosticsData.canInstall && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold mb-2 text-red-600 dark:text-red-400">❌ Почему кнопка "Установить" не работает:</h4>
                  <div className="text-xs text-gray-700 dark:text-gray-300 space-y-2">
                    <p>
                      <strong>Проблема:</strong> В Opera на мобильных устройствах событие <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">beforeinstallprompt</code> не срабатывает.
                    </p>
                    <p>
                      <strong>Причина:</strong> Opera не поддерживает программный API для установки PWA через JavaScript. Это ограничение браузера, а не ошибка кода.
                    </p>
                    <p>
                      <strong>Решение:</strong> Установка возможна только через виджет в меню браузера:
                    </p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Меню браузера (три точки) → "Добавить на главный экран"</li>
                      <li>В появившемся окне нажмите и удерживайте иконку виджета</li>
                      <li>Перетащите на главный экран</li>
                    </ol>
                    <p className="text-yellow-600 dark:text-yellow-400 font-medium">
                      ⚠️ Программная установка через кнопку "Установить" в Opera невозможна из-за ограничений браузера.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Блок отладки - показываем всегда для диагностики */}
      {showDebug && debugInfo && (
        <div className="fixed top-4 left-4 z-50 bg-yellow-100 dark:bg-yellow-900 p-3 rounded-lg shadow-lg text-xs max-w-xs border-2 border-yellow-500">
          <div className="font-bold mb-2 text-yellow-800 dark:text-yellow-200">PWA Debug Info:</div>
          <div className="space-y-1 text-yellow-900 dark:text-yellow-100">
            <div>canInstall: <span className="font-mono">{String(debugInfo.canInstall)}</span></div>
            <div>isInstalled: <span className="font-mono">{String(debugInfo.isInstalled)}</span></div>
            <div>isOpen: <span className="font-mono">{String(debugInfo.isOpen)}</span></div>
            <div>wasDismissed: <span className="font-mono">{String(debugInfo.wasDismissed)}</span></div>
            <div>consentAnswered: <span className="font-mono">{String(debugInfo.analyticsConsentAnswered)}</span></div>
            <div>consentValue: <span className="font-mono">{debugInfo.consentValue || 'null'}</span></div>
            <div>isBannerVisible: <span className="font-mono">{String(debugInfo.isBannerVisible)}</span></div>
            <div>isPromptVisible: <span className="font-mono">{String(debugInfo.isPromptVisible)}</span></div>
            <div className="font-bold mt-2 text-red-600 dark:text-red-400">
              shouldShow: <span className="font-mono">{String(debugInfo.shouldShow)}</span>
            </div>
            <button
              onClick={() => {
                diagnosePWAInstallation();
                setDiagnosticsVisible(true);
              }}
              className="mt-2 px-2 py-1 bg-yellow-600 dark:bg-yellow-700 text-white rounded text-xs hover:bg-yellow-700 dark:hover:bg-yellow-800"
            >
              🔍 Run Diagnostics
            </button>
            <button
              onClick={() => setDiagnosticsVisible(!diagnosticsVisible)}
              className="mt-1 px-2 py-1 bg-blue-600 dark:bg-blue-700 text-white rounded text-xs hover:bg-blue-700 dark:hover:bg-blue-800 w-full"
            >
              {diagnosticsVisible ? '📱 Hide Mobile Diagnostics' : '📱 Show Mobile Diagnostics'}
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('pwa_install_attempted_timestamp');
                localStorage.removeItem('pwa_prompt_dismissed');
                localStorage.removeItem('pwa_prompt_dismissed_until');
                window.location.reload();
              }}
              className="mt-1 px-2 py-1 bg-red-600 dark:bg-red-700 text-white rounded text-xs hover:bg-red-700 dark:hover:bg-red-800 w-full"
            >
              🔄 Reset Banner Flags
            </button>
          </div>
        </div>
      )}

      {/* Обновление доступно */}
      {updateAvailable && (
        <div className="fixed top-4 right-4 bg-blue-600 text-white p-4 rounded-lg shadow-lg z-50 max-w-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center space-x-2">
              <RefreshCw className="h-5 w-5" />
              <span>Доступно обновление</span>
            </h3>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm mb-3">
            Новая версия приложения готова к установке
          </p>
          <button
            onClick={handleUpdate}
            type="button"
            className="w-full bg-white text-blue-600 py-2 px-4 rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!updateAvailable}
          >
            Обновить сейчас
          </button>
        </div>
      )}

      {/* Промпт установки - баннер внизу экрана (не блокирует контент) */}
      {/* z-45 чтобы был выше BottomNavigation (z-40), но ниже баннера согласия на cookies (z-50) */}
      {isOpen && (
        <div 
          data-pwa-install-prompt
          className="fixed left-0 right-0 z-[45] bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg"
          style={{ 
            bottom: `${bottomNavHeight}px`,
            padding: '1rem',
            paddingBottom: `calc(1rem + env(safe-area-inset-bottom, 0))`
          }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <Download className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Установить приложение
                  </h3>
                  <button
                    onClick={handleClose}
                    className="flex-shrink-0 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    aria-label="Закрыть"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Установите Travel Map Platform для быстрого доступа и работы в офлайн режиме.
                </p>

                {isInstalling ? (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3">
                    <p className="text-sm text-blue-800 dark:text-blue-200 flex items-center space-x-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Подтвердите установку в появившемся окне браузера...</span>
                    </p>
                  </div>
                ) : instructions && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-3">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center space-x-2 text-sm">
                      {instructions.icon}
                      <span>{instructions.title}</span>
                    </h4>
                    <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      {instructions.steps.map((step, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="font-medium text-blue-600 dark:text-blue-400">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleInstall}
                    disabled={isInstalling}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    {isInstalling ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Установка...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        <span>Установить</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      diagnosePWAInstallation();
                      setDiagnosticsVisible(true);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                    title="Показать диагностику установки"
                  >
                    🔍 Диагностика
                  </button>
                  <button
                    onClick={handleClose}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                  >
                    Позже
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
